import * as THREE from 'three';
import { sb } from './supabase-client.js';
import { state } from './state.js';

/* ═══════════════════════════════════════════
   § 7 / § 9. NEARBY LOCATIONS & CINEMATIC CAMERA
   initNearbyLocations() wires the mobile panel
   toggle, builds loadLocations(), and exposes
   flyToLocation() on window (required because
   the location buttons use inline onclick handlers).
   Returns loadLocations so data-sync can call
   it on the first successful fetch.
   ═══════════════════════════════════════════ */

export function initNearbyLocations(active_project, ctx, { toggleMap, deselectAll }) {
  const { scene, camera, controls } = ctx;
  const $ = id => document.getElementById(id);

  // ── Mobile panel toggle ────────────────────
  $('btn-nearby-mobile').addEventListener('click', () => {
    $('loc-panel').classList.toggle('open-mobile');
  });

  // ── Click outside to close (Mobile) ────────
  document.addEventListener('pointerdown', (e) => {
    const panel = $('loc-panel');
    const btn = $('btn-nearby-mobile');
    if (panel && panel.classList.contains('open-mobile')) {
      if (!panel.contains(e.target) && (!btn || !btn.contains(e.target))) {
        panel.classList.remove('open-mobile');
      }
    }
  });

  // ── loadLocations ──────────────────────────
  async function loadLocations() {
    try {
      const { data, error } = await sb.from('locations').select('*').eq('project_id', active_project);
      if (error) throw error;
      state.locations = data || [];
      if (state.locations.length > 0) {
        $('loc-panel').classList.remove('hide');
        const list = $('loc-list');
        list.innerHTML = state.locations.map(l => `
          <button class="loc-btn" onclick="flyToLocation(${l.id})">
            <div class="loc-btn-icon"><img src="${l.icon_url || 'https://assets.plotos.in/global-assets/icons/default.png'}" onerror="this.style.display='none'"></div>
            <div class="loc-btn-text">
              <span class="loc-name">${l.name.toUpperCase()}</span>
              <span class="loc-dist">${l.distance_text}</span>
            </div>
          </button>
        `).join('');
      }
    } catch (e) { console.warn('[Supabase] Failed to load locations', e); }
  }

  // ── flyToLocation ──────────────────────────
  // Assigned to window because loc-btn HTML uses onclick="flyToLocation(id)"
  window.flyToLocation = function (id) {
    if (state.mapOn) toggleMap();
    deselectAll();

    const loc = state.locations.find(l => l.id === id);
    if (!loc) return;

    // Responsive scaling
    const isMobile = window.innerWidth < 1180;
    const pinScale = isMobile ? 10 : 16;
    const arcHeightMult = isMobile ? 0.10 : 0.15;
    const tubeRadius = isMobile ? 0.18 : 0.25;

    // Core Path Points
    const startPt = state.baseCenter.clone();
    startPt.y += 2;
    const endPt = new THREE.Vector3(loc.target_x || 0, loc.target_y || 0, loc.target_z || 0);
    const midPt = new THREE.Vector3().addVectors(startPt, endPt).multiplyScalar(0.5);
    midPt.y += Math.max(20, startPt.distanceTo(endPt) * arcHeightMult);

    // 1. Thin Elegant Laser Path
    const curve = new THREE.QuadraticBezierCurve3(startPt, midPt, endPt);
    const geometry = new THREE.TubeGeometry(curve, 150, tubeRadius, 6, false);
    geometry.setDrawRange(0, 0);
    const material = new THREE.MeshStandardMaterial({
      color: 0xc5a059,
      emissive: 0xc5a059,
      emissiveIntensity: 5,
      transparent: true,
      opacity: 0.9
    });
    state.currentPath = new THREE.Mesh(geometry, material);
    scene.add(state.currentPath);

    const totalPoints = geometry.index ? geometry.index.count : geometry.attributes.position.count;
    let startTime = performance.now();
    function animatePath() {
      const elapsed = performance.now() - startTime;
      const progress = Math.min(elapsed / 1500, 1.0);
      geometry.setDrawRange(0, Math.floor(totalPoints * progress));
      if (progress < 1.0) state.pathAnimFrame = requestAnimationFrame(animatePath);
    }
    animatePath();

    // 2. Floating Premium 3D Pin (scaled for device)
    const texLoader = new THREE.TextureLoader();
    texLoader.setCrossOrigin('anonymous');

    const finalIconUrl = loc.icon_url || 'https://assets.plotos.in/global-assets/icons/default.png';

    const spriteMat = new THREE.SpriteMaterial({
      map: texLoader.load(finalIconUrl + '?t=' + Date.now()),
      color: 0xffeab3,
      transparent: true,
      depthTest: false,
      opacity: 0
    });
    state.currentPin = new THREE.Sprite(spriteMat);
    state.currentPin.position.copy(endPt);
    state.currentPin.position.y += pinScale + 4;
    state.currentPin.scale.set(pinScale, pinScale, 1);
    state.currentPin.userData = { baseY: endPt.y + pinScale + 2 };
    scene.add(state.currentPin);

    // 2.5 Dynamic Ground Ripple / Wave (scaled for device)
    const rippleSize = isMobile ? 8 : 14;
    const rippleGeo = new THREE.RingGeometry(rippleSize - 1, rippleSize, 64);
    const rippleMat = new THREE.MeshBasicMaterial({
      color: 0xffd700,
      transparent: true,
      opacity: 0,
      side: THREE.DoubleSide,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    state.currentRipple = new THREE.Mesh(rippleGeo, rippleMat);
    state.currentRipple.rotation.x = -Math.PI / 2;
    state.currentRipple.position.copy(endPt);
    state.currentRipple.position.y += 0.5;
    scene.add(state.currentRipple);

    // 3. YAW-PRESERVING CINEMATIC CAMERA
    state.startCamPos.copy(camera.position);
    state.startCtrlTarget.copy(controls.target);
    state.startCamQuat.copy(camera.quaternion);

    // Frame both start and end points
    const frameBox = new THREE.Box3().expandByPoint(startPt).expandByPoint(endPt);
    const frameSize = frameBox.getSize(new THREE.Vector3());
    const frameCenter = frameBox.getCenter(new THREE.Vector3());
    const maxDim = Math.max(frameSize.x, frameSize.z, 80);

    const fov = camera.fov * (Math.PI / 180);
    let dist = (maxDim / 2) / Math.tan(fov / 2);
    if (window.innerWidth < window.innerHeight) dist /= (window.innerWidth / window.innerHeight);
    dist *= (isMobile ? 2.0 : 1.3);

    // Preserve user's current yaw
    const currentAzimuth = controls.getAzimuthalAngle();
    const pitch = Math.PI / 5; // ~36° gentle pitch
    const camPos = frameCenter.clone().add(new THREE.Vector3(
      dist * 0.7 * Math.sin(currentAzimuth) * Math.cos(pitch),
      dist * 0.55 * Math.sin(pitch),
      dist * 0.7 * Math.cos(currentAzimuth) * Math.cos(pitch)
    ));
    camPos.y = Math.max(camPos.y, 40);

    state.animTarget = frameCenter.clone();
    state.animCameraTarget = camPos;

    // Calculate final quaternion
    const tempCam = camera.clone();
    tempCam.position.copy(state.animCameraTarget);
    tempCam.lookAt(state.animTarget);
    state.endCamQuat.copy(tempCam.quaternion);

    state.animDurationScale = 2.2;
    controls.enabled = false;
    state.isAnimatingCamera = true;
    state.animTime = 0.01;

    if (isMobile) {
      $('loc-panel').classList.remove('open-mobile');
    }
  };

  return { loadLocations };
}
